import React, { useEffect, useState } from "react";
import {
    Dialog,
    DialogContent,
    Button,
    Box,
    Typography
} from "@mui/material";
import "./IndividualTripModal.css";
import CachedAvatar from "./CachedAvatar";
import { sendJoinRequest } from "../firebase/services/JoinRequests";
import { db } from "../firebase/firebase";
import { collection, query, where, getDocs } from "firebase/firestore";
import dayjs from "dayjs";
import { groupSizeOptions } from "../data/TravelOptions";
import { Icons } from "../data/Icons";


const IndividualTripModal = ({ isOpen, onClose, profile, formData, onOpenProfile, currentUserId, isNotification = false }) => {
    const [isRequestSent, setIsRequestSent] = useState(false);
    const [checkingRequest, setCheckingRequest] = useState(true);

    const groupSizeMap = Object.fromEntries(groupSizeOptions.map(opt => [opt.value, opt.label]));

    useEffect(() => {
        const checkExistingRequest = async () => {
            if (!profile?.id || !profile?.createdBy || !currentUserId) return;

            if (isNotification) { //To not show the button
                setCheckingRequest(true);
                return;
            }

            setCheckingRequest(true);
            try {
                const q = query(
                    collection(db, "joinRequests"),
                    where("fromUserId", "==", currentUserId),
                    where("toUserId", "==", profile.createdBy),
                    where("tripId", "==", profile.id)
                );

                const snapshot = await getDocs(q);
                setIsRequestSent(!snapshot.empty);
            } catch (error) {
                console.error("Error checking join request:", error);
            } finally {
                setCheckingRequest(false);
            }
        };

        if (isOpen) {
            checkExistingRequest();
        }
    }, [isOpen, profile, currentUserId]);

    const handleJoinRequest = async () => {
        if (!profile?.createdBy || !profile?.id || !currentUserId) {
            console.error("Missing required data to send join request.");
            return;
        }

        try {
            await sendJoinRequest({
                fromUserId: currentUserId,
                toUserId: profile.createdBy,
                tripId: profile.id
            });

            console.log("Join request and notification sent!");
            setIsRequestSent(true);
            onClose(); // The pop-up will close after sending the request
        } catch (error) {
            console.error("Failed to send join request:", error);
        }
    };

    if (!formData) return null;

    return (
        <Dialog
            sx={{ zIndex: 9999 }}
            open={isOpen}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{ className: "individual-trip-dialog" }}
        >
            <span className="individual-trip-close" onClick={onClose}>×</span>

            <DialogContent className="individual-trip-content">
                <div className="individual-trip-header">
                    <div>
                        <h3 className="individual-trip-title">
                            {profile.name || profile.title} {profile.age ? `(${profile.age})` : ""}
                        </h3>
                        <p className="individual-trip-location">{profile.location}</p>
                    </div>
                    <Box textAlign="center">
                        <CachedAvatar src={profile.image} alt={profile.title} className="individual-trip-avatar" />
                        {onOpenProfile && <button onClick={onOpenProfile} className="profile-text-button">
                            צפייה בפרופיל
                        </button>
                        }
                    </Box>
                </div>

                <Typography className="individual-trip-label">
                    מחפש/ת :
                </Typography>

                <p className="individual-trip-detail">
                    <strong>{Icons.date} תאריכים:</strong>{" "}
                    {formData.dates?.map((d) => {
                        const date = typeof d?.toDate === "function" ? d.toDate() : new Date(d);
                        return dayjs(date).format("DD/MM/YYYY");
                    }).join(", ")}
                </p>

                {Array.isArray(formData.region) && formData.region.filter(Boolean).length > 0 && (
                    <p className="individual-trip-detail">
                        <strong>{Icons.location} אזור:</strong> {formData.region.map(r => r?.label ?? r).join(", ")}
                    </p>
                )}

                {Array.isArray(formData.style) && formData.style.filter(Boolean).length > 0 && (
                    <p className="individual-trip-detail">
                        <strong>{Icons.style} סגנון:</strong> {formData.style.map(s => s?.label ?? s).join(", ")}
                    </p>
                )}

                {Array.isArray(formData.difficulty) && formData.difficulty.filter(Boolean).length > 0 && (
                    <p className="individual-trip-detail">
                        <strong>{Icons.difficulty} רמת קושי:</strong> {formData.difficulty.map(d => d?.label ?? d).join(", ")}
                    </p>
                )}

                {Array.isArray(formData.duration) && formData.duration.filter(Boolean).length > 0 && (
                    <p className="individual-trip-detail">
                        <strong>{Icons.duration} משך:</strong> {formData.duration.map(d => d?.label ?? d).join(", ")}
                    </p>
                )}

                {Array.isArray(formData.groupSize) && formData.groupSize.filter(Boolean).length > 0 && (
                    <p className="individual-trip-detail">
                        <strong>{Icons.groupSize} גודל קבוצה:</strong> {formData.groupSize.map(s => groupSizeMap[s] ?? s).join(", ")}
                    </p>
                )}

                {/* button "בקש להצטרף" */}
                {!checkingRequest && (
                    <Box mt={4} textAlign="center">
                        <Button
                            onClick={handleJoinRequest}
                            disabled={isRequestSent}
                            className={`individual-trip-button ${isRequestSent ? "sent" : ""}`}
                        >
                            {isRequestSent ? "בקשה נשלחה" : "בקש להצטרף"}
                        </Button>
                    </Box>
                )}
            </DialogContent>
        </Dialog>

    );
};

export default IndividualTripModal;
